/**
 * Dashboard page - High-level system overview.
 * Displays statistics, recent uploads, and quick actions.
 */

import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import {
  Router as SwitchIcon,
  Lan as VlanIcon,
  Cable as InterfaceIcon,
  CloudUpload,
  CheckCircle,
  Description as FileIcon,
  CheckCircleOutline as HealthIcon,
} from '@mui/icons-material';
import { api } from '@/services/api';
import type { Transaction } from '@/types/entities';

export const Dashboard: FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Statistics state
  const [stats, setStats] = useState({
    switchCount: 0,
    vlanCount: 0,
    interfaceCount: 0,
  });

  // System health state
  const [health, setHealth] = useState<{
    status: string;
    neo4j: string;
  } | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [switches, vlans, interfaces, recentTransactions, healthStatus] = await Promise.all([
          api.getSwitches(),
          api.getVlans(),
          api.getInterfaces(),
          api.getTransactions({ limit: 10 }),
          api.health(),
        ]);

        // Update statistics
        setStats({
          switchCount: switches.switches.length,
          vlanCount: vlans.vlans.length,
          interfaceCount: interfaces.interfaces.length,
        });

        // Update transactions
        setTransactions(recentTransactions.transactions);

        // Update health status
        setHealth({
          status: healthStatus.status,
          neo4j: healthStatus.neo4j,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load dashboard data'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={4}>
          <Card
            onClick={() => stats.switchCount > 0 && navigate('/switches?focus=true')}
            sx={{
              cursor: stats.switchCount > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': stats.switchCount > 0 ? {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              } : {},
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SwitchIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  {loading ? (
                    <Skeleton variant="text" width={60} height={60} />
                  ) : (
                    <>
                      <Typography variant="h3">{stats.switchCount}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Switches
                      </Typography>
                      {stats.switchCount === 0 && (
                        <Typography variant="caption" color="text.disabled">
                          Upload config to populate
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            onClick={() => stats.vlanCount > 0 && navigate('/vlans?focus=true')}
            sx={{
              cursor: stats.vlanCount > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': stats.vlanCount > 0 ? {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              } : {},
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VlanIcon sx={{ fontSize: 40, mr: 2, color: 'secondary.main' }} />
                <Box>
                  {loading ? (
                    <Skeleton variant="text" width={60} height={60} />
                  ) : (
                    <>
                      <Typography variant="h3">{stats.vlanCount}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total VLANs
                      </Typography>
                      {stats.vlanCount === 0 && (
                        <Typography variant="caption" color="text.disabled">
                          Upload config to populate
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            onClick={() => stats.interfaceCount > 0 && navigate('/interfaces?focus=true')}
            sx={{
              cursor: stats.interfaceCount > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': stats.interfaceCount > 0 ? {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              } : {},
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InterfaceIcon sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
                <Box>
                  {loading ? (
                    <Skeleton variant="text" width={60} height={60} />
                  ) : (
                    <>
                      <Typography variant="h3">{stats.interfaceCount}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Interfaces
                      </Typography>
                      {stats.interfaceCount === 0 && (
                        <Typography variant="caption" color="text.disabled">
                          Upload config to populate
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HealthIcon
                    sx={{
                      fontSize: 32,
                      mr: 2,
                      color: health?.neo4j === 'connected' ? 'success.main' : 'error.main'
                    }}
                  />
                  <Box>
                    <Typography variant="h6">System Health</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {loading ? (
                        <Skeleton variant="text" width={150} />
                      ) : health ? (
                        <>
                          Backend: {health.status} | Neo4j: {health.neo4j}
                        </>
                      ) : (
                        'Unable to fetch health status'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  color="primary"
                  onClick={() => navigate('/transactions')}
                >
                  Upload Configuration
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CheckCircle />}
                  color="secondary"
                  onClick={() => navigate('/connectivity')}
                >
                  Check Connectivity
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Uploads */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Uploads
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent uploads. Upload a configuration file to get started.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Filename</TableCell>
                        <TableCell>Uploaded</TableCell>
                        <TableCell>Uploader</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.transactionId} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <FileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              {transaction.filename}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(transaction.uploadTimestamp)}
                          </TableCell>
                          <TableCell>{transaction.uploaderUsername}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  transaction.status === 'completed'
                                    ? 'success.main'
                                    : 'warning.main',
                                fontWeight: 'medium',
                              }}
                            >
                              {transaction.status}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
